import React, {
  useEffect,
  useState,
  useRef
} from "react";

import axios from "axios";

import {
  Container,
  Card,
  Form,
  Button,
  Row,
  Col,
  InputGroup,
  Spinner,
  Alert,
  Badge
} from "react-bootstrap";


/* ============================================================
   API CONFIGURATION
   ============================================================ */

const API_URL =
  process.env.REACT_APP_API_URL ||
  "https://apimyshop.myidealtd.co.uk/api";


/* ============================================================
   SERVER URL

   Example:

   API_URL:
   https://apimyshop.myidealtd.co.uk/api

   SERVER_URL:
   https://apimyshop.myidealtd.co.uk
   ============================================================ */

const SERVER_URL =
  API_URL.replace(/\/api\/?$/, "");


/* ============================================================
   EMPTY FORM
   ============================================================ */

const emptyForm = {
  title: "",
  message: "",
  mediaType: "image",
  media: "",
  buttonText: "",
  buttonLink: "",
  active: true
};


/* ============================================================
   COMPONENT
   ============================================================ */

function ManageBanners() {

  /* ==========================================================
     STATE
     ========================================================== */

  const [form, setForm] =
    useState(emptyForm);

  const [banners, setBanners] =
    useState([]);

  const [loadingBanners, setLoadingBanners] =
    useState(true);

  const [uploading, setUploading] =
    useState(false);

  const [saving, setSaving] =
    useState(false);

  const [deletingId, setDeletingId] =
    useState(null);

  const [editingId, setEditingId] =
    useState(null);

  const [previewUrl, setPreviewUrl] =
    useState("");

  const [error, setError] =
    useState("");

  const [success, setSuccess] =
    useState("");

  const fileInputRef =
    useRef(null);


  /* ==========================================================
     MEDIA URL

     Converts:

     /uploads/banner.jpg

     into:

     https://domain.com/uploads/banner.jpg

     Also supports:

     uploads/banner.jpg
     http://...
     https://...
     //domain.com/...
     blob:...
     ========================================================== */

  const getMediaUrl = (
    media,
    cacheKey = null
  ) => {

    if (!media) {
      return "";
    }

    let url = media;


    /* ----------------------------------------------------------
       BLOB URL
       ---------------------------------------------------------- */

    if (
      media.startsWith("blob:")
    ) {

      return media;

    }


    /* ----------------------------------------------------------
       HTTP / HTTPS
       ---------------------------------------------------------- */

    if (
      media.startsWith("http://") ||
      media.startsWith("https://")
    ) {

      url = media;

    }


    /* ----------------------------------------------------------
       PROTOCOL RELATIVE
       ---------------------------------------------------------- */

    else if (
      media.startsWith("//")
    ) {

      url =
        `${window.location.protocol}${media}`;

    }


    /* ----------------------------------------------------------
       ROOT RELATIVE

       /uploads/banner.jpg
       ---------------------------------------------------------- */

    else if (
      media.startsWith("/")
    ) {

      url =
        `${SERVER_URL}${media}`;

    }


    /* ----------------------------------------------------------
       RELATIVE

       uploads/banner.jpg
       ---------------------------------------------------------- */

    else {

      url =
        `${SERVER_URL}/${media}`;

    }


    /* ----------------------------------------------------------
       CACHE BUSTING

       Only add cache bust when requested.

       IMPORTANT:
       Do NOT use Date.now() on every render because that
       would force the browser to reload the image constantly.
       ---------------------------------------------------------- */

    if (
      cacheKey &&
      !url.startsWith("blob:")
    ) {

      const separator =
        url.includes("?")
          ? "&"
          : "?";

      url =
        `${url}${separator}v=${encodeURIComponent(
          cacheKey
        )}`;

    }


    return url;

  };


  /* ==========================================================
     LOAD BANNERS
     ========================================================== */

  const loadBanners = async () => {

    try {

      setLoadingBanners(true);

      setError("");


      const response =
        await axios.get(
          `${API_URL}/banners`
        );


      console.log(
        "===================================="
      );

      console.log(
        "BANNERS FROM BACKEND:"
      );

      console.log(
        response.data
      );

      console.log(
        "===================================="
      );


      let bannerData = [];


      /* --------------------------------------------------------
         BACKEND ARRAY
         -------------------------------------------------------- */

      if (
        Array.isArray(
          response.data
        )
      ) {

        bannerData =
          response.data;

      }


      /* --------------------------------------------------------
         { banners: [] }
         -------------------------------------------------------- */

      else if (
        Array.isArray(
          response.data?.banners
        )
      ) {

        bannerData =
          response.data.banners;

      }


      /* --------------------------------------------------------
         { data: [] }
         -------------------------------------------------------- */

      else if (
        Array.isArray(
          response.data?.data
        )
      ) {

        bannerData =
          response.data.data;

      }


      /* --------------------------------------------------------
         SET BANNERS
         -------------------------------------------------------- */

      setBanners(
        bannerData
      );


      /* --------------------------------------------------------
         DEBUG MEDIA
         -------------------------------------------------------- */

      bannerData.forEach(
        (banner, index) => {

          console.log(
            `Banner ${index + 1}:`,
            {
              id:
                banner?._id ||
                banner?.id,

              title:
                banner?.title,

              media:
                banner?.media,

              mediaType:
                banner?.mediaType,

              updatedAt:
                banner?.updatedAt
            }
          );

        }
      );

    }
    catch (err) {

      console.error(
        "Load Banners Error:",
        err.response?.data ||
        err.message
      );


      setError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Failed to load banners."
      );

    }
    finally {

      setLoadingBanners(false);

    }

  };


  /* ==========================================================
     INITIAL LOAD
     ========================================================== */

  useEffect(() => {

    loadBanners();

  }, []);


  /* ==========================================================
     CLEAN BLOB URL

     Prevent memory leaks from local previews.
     ========================================================== */

  useEffect(() => {

    return () => {

      if (
        previewUrl &&
        previewUrl.startsWith("blob:")
      ) {

        URL.revokeObjectURL(
          previewUrl
        );

      }

    };

  }, [previewUrl]);


  /* ==========================================================
     FORM CHANGE
     ========================================================== */

  const handleChange = (e) => {

    const {
      name,
      value,
      type,
      checked
    } = e.target;


    /* ----------------------------------------------------------
       MEDIA TYPE CHANGE
       ---------------------------------------------------------- */

    if (
      name === "mediaType" &&
      value !== form.mediaType
    ) {

      setForm(
        (prev) => ({
          ...prev,
          mediaType:
            value,
          media: ""
        })
      );

      setPreviewUrl("");

      return;

    }


    /* ----------------------------------------------------------
       NORMAL CHANGE
       ---------------------------------------------------------- */

    setForm(
      (prev) => ({
        ...prev,

        [name]:
          type === "checkbox"
            ? checked
            : value
      })
    );

  };


  /* ==========================================================
     OPEN FILE PICKER
     ========================================================== */

  const openFilePicker = () => {

    if (
      fileInputRef.current
    ) {

      fileInputRef.current.click();

    }

  };


  /* ==========================================================
     FILE SELECTION + UPLOAD
     ========================================================== */

  const handleFileSelect =
    async (e) => {

      const file =
        e.target.files?.[0];


      if (!file) {
        return;
      }


      setError("");
      setSuccess("");


      /* --------------------------------------------------------
         VALIDATE IMAGE
         -------------------------------------------------------- */

      if (
        form.mediaType === "image"
      ) {

        if (
          !file.type.startsWith(
            "image/"
          )
        ) {

          setError(
            "Please select a valid image file."
          );

          e.target.value = "";

          return;

        }

      }


      /* --------------------------------------------------------
         VALIDATE VIDEO
         -------------------------------------------------------- */

      if (
        form.mediaType === "video"
      ) {

        if (
          !file.type.startsWith(
            "video/"
          )
        ) {

          setError(
            "Please select a valid video file."
          );

          e.target.value = "";

          return;

        }

      }


      /* --------------------------------------------------------
         SIZE CHECK
         -------------------------------------------------------- */

      const maxSize =
        100 * 1024 * 1024;


      if (
        file.size > maxSize
      ) {

        setError(
          "File is too large. Maximum size is 100 MB."
        );

        e.target.value = "";

        return;

      }


      /* ========================================================
         LOCAL PREVIEW
         ======================================================== */

      const localPreview =
        URL.createObjectURL(
          file
        );


      setPreviewUrl(
        localPreview
      );


      /* ========================================================
         UPLOAD
         ======================================================== */

      try {

        setUploading(true);


        const formData =
          new FormData();


        formData.append(
          "file",
          file
        );


        console.log(
          "Uploading banner media:",
          file.name
        );


        const response =
          await axios.post(
            `${API_URL}/banners/upload`,
            formData,
            {
              headers: {
                "Content-Type":
                  "multipart/form-data"
              }
            }
          );


        console.log(
          "===================================="
        );

        console.log(
          "UPLOAD RESPONSE:"
        );

        console.log(
          response.data
        );

        console.log(
          "===================================="
        );


        /* ------------------------------------------------------
           GET MEDIA URL FROM RESPONSE
           ------------------------------------------------------ */

        const uploadedUrl =
          response.data?.url ||
          response.data?.media ||
          response.data?.fileUrl ||
          response.data?.path ||
          response.data?.file?.url ||
          response.data?.data?.url;


        if (!uploadedUrl) {

          throw new Error(
            "Upload succeeded but backend did not return a media URL."
          );

        }


        console.log(
          "NEW MEDIA URL:",
          uploadedUrl
        );


        /* ======================================================
           SAVE MEDIA URL INTO FORM
           ====================================================== */

        setForm(
          (prev) => ({
            ...prev,
            media:
              uploadedUrl
          })
        );


        /* ======================================================
           SHOW BACKEND IMAGE

           Use timestamp ONLY ONCE after upload.
           ====================================================== */

        const cacheKey =
          Date.now();


        const backendPreview =
          getMediaUrl(
            uploadedUrl,
            cacheKey
          );


        console.log(
          "NEW PREVIEW URL:",
          backendPreview
        );


        setPreviewUrl(
          backendPreview
        );


        setSuccess(
          "New media uploaded successfully. Click UPDATE BANNER to save it."
        );

      }
      catch (err) {

        console.error(
          "Upload Error:",
          err.response?.data ||
          err.message
        );


        setError(
          err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Failed to upload media."
        );

      }
      finally {

        setUploading(false);


        /* ------------------------------------------------------
           RESET FILE INPUT

           Allows selecting the same file again.
           ------------------------------------------------------ */

        e.target.value = "";

      }

    };


  /* ==========================================================
     RESET FORM
     ========================================================== */

  const resetForm = () => {

    setForm({
      ...emptyForm
    });


    setEditingId(
      null
    );


    setPreviewUrl("");


    setError("");


    if (
      fileInputRef.current
    ) {

      fileInputRef.current.value =
        "";

    }

  };


  /* ==========================================================
     EDIT BANNER
     ========================================================== */

  const handleEdit = (
    banner
  ) => {

    const id =
      banner?._id ||
      banner?.id;


    if (!id) {

      setError(
        "This banner does not have a valid ID."
      );

      return;

    }


    console.log(
      "===================================="
    );

    console.log(
      "EDITING BANNER:"
    );

    console.log(
      banner
    );

    console.log(
      "===================================="
    );


    setEditingId(
      id
    );


    const mediaType =
      banner.mediaType ||
      "image";


    const media =
      banner.media ||
      "";


    setForm({

      title:
        banner.title || "",

      message:
        banner.message || "",

      mediaType:
        mediaType,

      media:
        media,

      buttonText:
        banner.buttonText || "",

      buttonLink:
        banner.buttonLink || "",

      active:
        banner.active !== false

    });


    /* ----------------------------------------------------------
       EXISTING IMAGE PREVIEW

       Use updatedAt as cache key.

       If updatedAt does not exist, use the media URL itself.
       ---------------------------------------------------------- */

    const cacheKey =
      banner.updatedAt ||
      banner.updated_at ||
      media;


    setPreviewUrl(
      getMediaUrl(
        media,
        cacheKey
      )
    );


    setError("");


    setSuccess(
      "Banner loaded for editing."
    );


    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });

  };


  /* ==========================================================
     SAVE / UPDATE BANNER
     ========================================================== */

  const saveBanner =
    async (e) => {

      e.preventDefault();


      setError("");
      setSuccess("");


      /* --------------------------------------------------------
         REQUIRE MEDIA
         -------------------------------------------------------- */

      if (
        !form.media
      ) {

        setError(
          "Please select an image or video."
        );

        return;

      }


      try {

        setSaving(true);


        /* ======================================================
           PAYLOAD
           ====================================================== */

        const payload = {

          title:
            form.title,

          message:
            form.message,

          mediaType:
            form.mediaType,

          media:
            form.media,

          buttonText:
            form.buttonText,

          buttonLink:
            form.buttonLink,

          active:
            form.active

        };


        console.log(
          "===================================="
        );

        console.log(
          "SENDING BANNER UPDATE:"
        );

        console.log(
          "ID:",
          editingId
        );

        console.log(
          "MEDIA:",
          payload.media
        );

        console.log(
          "MEDIA TYPE:",
          payload.mediaType
        );

        console.log(
          "FULL PAYLOAD:",
          payload
        );

        console.log(
          "===================================="
        );


        let response;


        /* ======================================================
           UPDATE
           ====================================================== */

        if (
          editingId
        ) {

          response =
            await axios.put(
              `${API_URL}/banners/${editingId}`,
              payload
            );


          console.log(
            "===================================="
          );

          console.log(
            "BANNER UPDATE RESPONSE:"
          );

          console.log(
            response.data
          );

          console.log(
            "===================================="
          );


          /* ----------------------------------------------------
             CHECK RETURNED MEDIA
             ---------------------------------------------------- */

          const updatedBanner =
            response.data?.banner ||
            response.data?.data ||
            response.data;


          console.log(
            "UPDATED BANNER MEDIA:",
            updatedBanner?.media
          );


          if (
            updatedBanner?.media &&
            updatedBanner.media !== form.media
          ) {

            console.warn(
              "WARNING: Backend returned a different media URL than the one sent."
            );

            console.warn(
              "Sent:",
              form.media
            );

            console.warn(
              "Returned:",
              updatedBanner.media
            );

          }


          setSuccess(
            "Banner updated successfully."
          );

        }


        /* ======================================================
           CREATE
           ====================================================== */

        else {

          response =
            await axios.post(
              `${API_URL}/banners`,
              payload
            );


          console.log(
            "Banner created:",
            response.data
          );


          setSuccess(
            "Banner created successfully."
          );

        }


        /* ======================================================
           RESET FORM
           ====================================================== */

        resetForm();


        /* ======================================================
           RELOAD FROM BACKEND

           This confirms that the database contains the
           updated media URL.
           ====================================================== */

        await loadBanners();

      }
      catch (err) {

        console.error(
          "===================================="
        );

        console.error(
          "SAVE BANNER ERROR:"
        );

        console.error(
          err.response?.data ||
          err.message
        );

        console.error(
          "===================================="
        );


        setError(
          err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Failed to save banner."
        );

      }
      finally {

        setSaving(false);

      }

    };


  /* ==========================================================
     DELETE BANNER
     ========================================================== */

  const handleDelete =
    async (banner) => {

      const id =
        banner?._id ||
        banner?.id;


      if (!id) {

        setError(
          "Banner ID is missing."
        );

        return;

      }


      const confirmed =
        window.confirm(
          `Are you sure you want to delete "${banner.title || "this banner"}"?`
        );


      if (!confirmed) {
        return;
      }


      try {

        setDeletingId(
          id
        );

        setError("");
        setSuccess("");


        await axios.delete(
          `${API_URL}/banners/${id}`
        );


        /* ------------------------------------------------------
           REMOVE IMMEDIATELY
           ------------------------------------------------------ */

        setBanners(
          (prev) =>
            prev.filter(
              (item) =>
                (
                  item?._id ||
                  item?.id
                ) !== id
            )
        );


        /* ------------------------------------------------------
           IF CURRENTLY EDITING
           ------------------------------------------------------ */

        if (
          editingId === id
        ) {

          resetForm();

        }


        setSuccess(
          "Banner deleted successfully."
        );

      }
      catch (err) {

        console.error(
          "Delete Error:",
          err.response?.data ||
          err.message
        );


        setError(
          err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Failed to delete banner."
        );

      }
      finally {

        setDeletingId(
          null
        );

      }

    };


  /* ==========================================================
     RENDER EXISTING BANNER MEDIA
     ========================================================== */

  const renderBannerMedia =
    (banner) => {

      const media =
        banner?.media;


      if (!media) {

        return (
          <div
            className="d-flex align-items-center justify-content-center bg-light"
            style={{
              height: "180px"
            }}
          >
            <span className="text-muted">
              No media
            </span>
          </div>
        );

      }


      /* --------------------------------------------------------
         CACHE KEY

         Prefer updatedAt because it changes when the banner
         is updated.

         This is much better than Date.now() here.
         -------------------------------------------------------- */

      const cacheKey =
        banner.updatedAt ||
        banner.updated_at ||
        media;


      const mediaUrl =
        getMediaUrl(
          media,
          cacheKey
        );


      /* --------------------------------------------------------
         VIDEO
         -------------------------------------------------------- */

      if (
        banner.mediaType ===
        "video"
      ) {

        return (
          <video
            key={mediaUrl}
            src={mediaUrl}
            controls
            muted
            playsInline
            style={{
              width: "100%",
              height: "180px",
              objectFit: "cover"
            }}
            onError={() => {

              console.error(
                "Banner video failed:",
                mediaUrl
              );

            }}
          />
        );

      }


      /* --------------------------------------------------------
         IMAGE
         -------------------------------------------------------- */

      return (
        <img
          key={mediaUrl}
          src={mediaUrl}
          alt={
            banner.title ||
            "Banner"
          }
          style={{
            width: "100%",
            height: "180px",
            objectFit: "cover"
          }}
          onLoad={() => {

            console.log(
              "Banner image loaded:",
              mediaUrl
            );

          }}
          onError={() => {

            console.error(
              "Banner image failed:",
              mediaUrl
            );

          }}
        />
      );

    };


  /* ==========================================================
     MEDIA PREVIEW
     ========================================================== */

  const renderPreview = () => {

    if (!previewUrl) {

      return (
        <div
          className="text-muted text-center py-4"
        >
          No media selected.
        </div>
      );

    }


    /* --------------------------------------------------------
       VIDEO
       -------------------------------------------------------- */

    if (
      form.mediaType ===
      "video"
    ) {

      return (
        <video
          key={previewUrl}
          src={previewUrl}
          controls
          playsInline
          style={{
            width: "100%",
            maxHeight: "350px",
            objectFit: "contain",
            background: "#000"
          }}
          onError={() => {

            console.error(
              "Video preview failed:",
              previewUrl
            );

          }}
        />
      );

    }


    /* --------------------------------------------------------
       IMAGE
       -------------------------------------------------------- */

    return (
      <img
        key={previewUrl}
        src={previewUrl}
        alt="Banner Preview"
        style={{
          width: "100%",
          maxHeight: "350px",
          objectFit: "contain"
        }}
        onLoad={() => {

          console.log(
            "Preview loaded:",
            previewUrl
          );

        }}
        onError={() => {

          console.error(
            "Image preview failed:",
            previewUrl
          );

        }}
      />
    );

  };


  /* ==========================================================
     ACCEPTED FILES
     ========================================================== */

  const acceptedFiles =
    form.mediaType === "image"
      ? "image/*"
      : "video/*";


  /* ==========================================================
     RENDER
     ========================================================== */

  return (

    <Container
      className="mt-4 mb-5"
    >


      {/* ======================================================
          EXISTING BANNERS
          ====================================================== */}

      <Card
        className="shadow mb-4"
      >

        <Card.Body>


          {/* --------------------------------------------------
              HEADER
              -------------------------------------------------- */}

          <div
            className="d-flex justify-content-between align-items-center mb-4"
          >

            <div>

              <h4 className="mb-1">
                Existing Banners
              </h4>

              <div className="text-muted">

                {banners.length} banner
                {banners.length !== 1
                  ? "s"
                  : ""}{" "}
                found

              </div>

            </div>


            <Button
              variant="outline-primary"
              onClick={
                loadBanners
              }
              disabled={
                loadingBanners
              }
            >

              {loadingBanners ? (

                <>

                  <Spinner
                    animation="border"
                    size="sm"
                    className="me-2"
                  />

                  Loading...

                </>

              ) : (

                "↻ Refresh"

              )}

            </Button>

          </div>


          {/* --------------------------------------------------
              LOADING
              -------------------------------------------------- */}

          {loadingBanners && (

            <div
              className="text-center py-5"
            >

              <Spinner
                animation="border"
              />

              <div className="mt-2 text-muted">
                Loading banners...
              </div>

            </div>

          )}


          {/* --------------------------------------------------
              NO BANNERS
              -------------------------------------------------- */}

          {!loadingBanners &&
            banners.length === 0 && (

              <Alert
                variant="info"
              >
                No banners found.
              </Alert>

            )}


          {/* --------------------------------------------------
              BANNERS
              -------------------------------------------------- */}

          {!loadingBanners &&
            banners.length > 0 && (

              <Row>

                {banners.map(
                  (
                    banner,
                    index
                  ) => {

                    const id =
                      banner?._id ||
                      banner?.id;


                    const isDeleting =
                      deletingId === id;


                    const isEditing =
                      editingId === id;


                    return (

                      <Col
                        key={
                          id ||
                          index
                        }
                        md={6}
                        lg={4}
                        className="mb-4"
                      >

                        <Card
                          className={`h-100 shadow-sm ${
                            isEditing
                              ? "border-primary"
                              : ""
                          }`}
                        >


                          {/* MEDIA */}

                          {renderBannerMedia(
                            banner
                          )}


                          <Card.Body>


                            {/* TITLE / STATUS */}

                            <div
                              className="d-flex justify-content-between align-items-start gap-2"
                            >

                              <Card.Title>

                                {banner.title ||
                                  "Untitled Banner"}

                              </Card.Title>


                              <Badge
                                bg={
                                  banner.active
                                    ? "success"
                                    : "secondary"
                                }
                              >

                                {banner.active
                                  ? "Active"
                                  : "Inactive"}

                              </Badge>

                            </div>


                            {/* MESSAGE */}

                            {banner.message && (

                              <Card.Text>
                                {banner.message}
                              </Card.Text>

                            )}


                            {/* BUTTON TEXT */}

                            {banner.buttonText && (

                              <div className="small mb-1">

                                <strong>
                                  Button:
                                </strong>{" "}

                                {
                                  banner.buttonText
                                }

                              </div>

                            )}


                            {/* BUTTON LINK */}

                            {banner.buttonLink && (

                              <div
                                className="small text-muted mb-3"
                              >

                                <strong>
                                  Link:
                                </strong>{" "}

                                {
                                  banner.buttonLink
                                }

                              </div>

                            )}


                            {/* UPDATED TIME */}

                            {banner.updatedAt && (

                              <div
                                className="small text-muted mb-3"
                              >

                                Last updated:{" "}

                                {new Date(
                                  banner.updatedAt
                                ).toLocaleString()}

                              </div>

                            )}


                            {/* ACTION BUTTONS */}

                            <div
                              className="d-flex gap-2 mt-3"
                            >

                              <Button
                                variant={
                                  isEditing
                                    ? "primary"
                                    : "outline-primary"
                                }
                                size="sm"
                                className="flex-grow-1"
                                onClick={() =>
                                  handleEdit(
                                    banner
                                  )
                                }
                                disabled={
                                  isDeleting
                                }
                              >

                                ✏️{" "}

                                {isEditing
                                  ? "Editing"
                                  : "Edit"}

                              </Button>


                              <Button
                                variant="outline-danger"
                                size="sm"
                                className="flex-grow-1"
                                onClick={() =>
                                  handleDelete(
                                    banner
                                  )
                                }
                                disabled={
                                  isDeleting
                                }
                              >

                                {isDeleting ? (

                                  <>

                                    <Spinner
                                      animation="border"
                                      size="sm"
                                      className="me-1"
                                    />

                                    Deleting...

                                  </>

                                ) : (

                                  "🗑 Delete"

                                )}

                              </Button>

                            </div>

                          </Card.Body>

                        </Card>

                      </Col>

                    );

                  }
                )}

              </Row>

            )}

        </Card.Body>

      </Card>


      {/* ======================================================
          CREATE / EDIT
          ====================================================== */}

      <Card
        className="shadow p-4"
      >


        {/* ----------------------------------------------------
            HEADER
            ---------------------------------------------------- */}

        <div
          className="d-flex justify-content-between align-items-center mb-4"
        >

          <Card.Title
            className="mb-0"
          >

            {editingId
              ? "✏️ Edit Banner"
              : "🚀 Create New Banner"}

          </Card.Title>


          {editingId && (

            <Button
              variant="outline-secondary"
              size="sm"
              onClick={
                resetForm
              }
            >

              ✕ Cancel Edit

            </Button>

          )}

        </div>


        {/* ====================================================
            ALERTS
            ==================================================== */}

        {error && (

          <Alert
            variant="danger"
            dismissible
            onClose={() =>
              setError("")
            }
          >

            {error}

          </Alert>

        )}


        {success && (

          <Alert
            variant="success"
            dismissible
            onClose={() =>
              setSuccess("")
            }
          >

            {success}

          </Alert>

        )}


        {/* ====================================================
            FORM
            ==================================================== */}

        <Form
          onSubmit={
            saveBanner
          }
        >


          {/* ==================================================
              TITLE / MESSAGE
              ================================================== */}

          <Row>

            <Col md={6}>

              <Form.Group
                className="mb-3"
              >

                <Form.Label>
                  Title
                </Form.Label>

                <Form.Control
                  type="text"
                  name="title"
                  value={
                    form.title
                  }
                  onChange={
                    handleChange
                  }
                  required
                />

              </Form.Group>

            </Col>


            <Col md={6}>

              <Form.Group
                className="mb-3"
              >

                <Form.Label>
                  Message
                </Form.Label>

                <Form.Control
                  type="text"
                  name="message"
                  value={
                    form.message
                  }
                  onChange={
                    handleChange
                  }
                  required
                />

              </Form.Group>

            </Col>

          </Row>


          {/* ==================================================
              MEDIA TYPE / MEDIA
              ================================================== */}

          <Row>

            <Col md={4}>

              <Form.Group
                className="mb-3"
              >

                <Form.Label>
                  Media Type
                </Form.Label>

                <Form.Select
                  name="mediaType"
                  value={
                    form.mediaType
                  }
                  onChange={
                    handleChange
                  }
                >

                  <option value="image">
                    Image
                  </option>

                  <option value="video">
                    Video
                  </option>

                </Form.Select>

              </Form.Group>

            </Col>


            <Col md={8}>

              <Form.Group
                className="mb-3"
              >

                <Form.Label>
                  Media
                </Form.Label>


                <InputGroup>

                  <Form.Control
                    type="text"
                    name="media"
                    value={
                      form.media
                    }
                    onChange={
                      handleChange
                    }
                    placeholder={
                      form.mediaType ===
                      "image"
                        ? "/uploads/banner.jpg"
                        : "/uploads/banner.mp4"
                    }
                  />


                  <Button
                    variant="outline-primary"
                    type="button"
                    onClick={
                      openFilePicker
                    }
                    disabled={
                      uploading
                    }
                  >

                    {uploading ? (

                      <>

                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />

                        Uploading...

                      </>

                    ) : (

                      "📁 Choose File"

                    )}

                  </Button>

                </InputGroup>


                <Form.Control
                  ref={
                    fileInputRef
                  }
                  type="file"
                  accept={
                    acceptedFiles
                  }
                  onChange={
                    handleFileSelect
                  }
                  style={{
                    display: "none"
                  }}
                />


                <Form.Text
                  className="text-muted"
                >

                  Choose a new{" "}
                  {form.mediaType}{" "}
                  to replace the current media.

                </Form.Text>

              </Form.Group>

            </Col>

          </Row>


          {/* ==================================================
              MEDIA PREVIEW
              ================================================== */}

          <Card
            className="mb-4"
          >

            <Card.Body>

              <div
                className="d-flex justify-content-between align-items-center mb-3"
              >

                <Card.Title
                  className="fs-6 mb-0"
                >
                  Media Preview
                </Card.Title>


                {uploading && (

                  <Badge
                    bg="primary"
                  >
                    Uploading...
                  </Badge>

                )}

              </div>


              {renderPreview()}

            </Card.Body>

          </Card>


          {/* ==================================================
              BUTTON
              ================================================== */}

          <Row>

            <Col md={6}>

              <Form.Group
                className="mb-3"
              >

                <Form.Label>
                  Button Text
                </Form.Label>

                <Form.Control
                  type="text"
                  name="buttonText"
                  placeholder="Explore"
                  value={
                    form.buttonText
                  }
                  onChange={
                    handleChange
                  }
                />

              </Form.Group>

            </Col>


            <Col md={6}>

              <Form.Group
                className="mb-3"
              >

                <Form.Label>
                  Button Link
                </Form.Label>

                <Form.Control
                  type="text"
                  name="buttonLink"
                  placeholder="/products"
                  value={
                    form.buttonLink
                  }
                  onChange={
                    handleChange
                  }
                />

              </Form.Group>

            </Col>

          </Row>


          {/* ==================================================
              ACTIVE
              ================================================== */}

          <Form.Group
            className="mb-3"
          >

            <Form.Check
              type="checkbox"
              label="Active"
              name="active"
              checked={
                form.active
              }
              onChange={
                handleChange
              }
            />

          </Form.Group>


          {/* ==================================================
              SAVE / UPDATE
              ================================================== */}

          <Button
            variant={
              editingId
                ? "warning"
                : "primary"
            }
            type="submit"
            className="w-100"
            disabled={
              saving ||
              uploading
            }
          >

            {saving ? (

              <>

                <Spinner
                  animation="border"
                  size="sm"
                  className="me-2"
                />

                {editingId
                  ? "UPDATING..."
                  : "SAVING..."}

              </>

            ) : (

              editingId
                ? "UPDATE BANNER"
                : "SAVE BANNER"

            )}

          </Button>


          {/* ==================================================
              CANCEL
              ================================================== */}

          {editingId && (

            <Button
              variant="outline-secondary"
              type="button"
              className="w-100 mt-2"
              onClick={
                resetForm
              }
              disabled={
                saving ||
                uploading
              }
            >

              CANCEL EDIT

            </Button>

          )}

        </Form>

      </Card>

    </Container>

  );

}


export default ManageBanners;